import React, { useState, useEffect } from 'react';

const WorkerError = ({ errorData }) => {
  const [countdown, setCountdown] = useState(30);
  const [isRetrying, setIsRetrying] = useState(false);

  useEffect(() => {
    // Auto-retry countdown
    const timer = setInterval(() => {
      setCountdown(prev => {
        if (prev <= 1) {
          handleRetry();
          return 30;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  const handleRetry = async () => {
    setIsRetrying(true);
    try {
      // Check if worker is running using the public endpoint
      const response = await fetch('/api/worker-status', {
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();

        if (data.data.worker_running) {
          // Worker is running again, reload the page
          window.location.reload();
          return;
        }
      }

      // Still not running, reset countdown
      setCountdown(30);
    } catch (error) {
      // no-op
    } finally {
      setIsRetrying(false);
    }
  };

  const handleContactSupport = () => {
    window.open('mailto:support@smartemailverifier.com?subject=Smart Email Verifier - Worker Not Running', '_blank');
  };

  const getErrorTitle = () => {
    if (!errorData) return 'Application Not Active';

    switch (errorData.code) {
      case 'WORKER_NOT_RUNNING':
        return 'Worker Crashed unexpected , please try command : pm2 restart all, if not fixed contact support';
      case 'LICENSE_INVALID':
        return 'License Not Activated';
      case 'SYSTEM_ERROR':
        return 'System Error';
      default:
        return 'Application Not Active';
    }
  };

  const getErrorDescription = () => {
    if (!errorData) return 'The application not running properly. This may be due system problems.';

    switch (errorData.code) {
      case 'WORKER_NOT_RUNNING':
        return 'The worker process has stopped. This may be due to system errors, or configuration problems.';
      case 'LICENSE_INVALID':
        return 'Your application configuration might be wrong. The worker has been crashed';
      case 'SYSTEM_ERROR':
        return 'A system error has occurred and the worker has stopped. This may be due to database issues or configuration problems.';
      default:
        return 'The worker is not running. This may be due system problems.';
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-red-50 to-red-100 flex items-center justify-center p-4">
      <div className="max-w-2xl w-full bg-white dark:bg-gray-900 dark:border dark:border-gray-800 rounded-2xl shadow-2xl p-8">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="mx-auto w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mb-4">
            <svg className="w-10 h-10 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            {getErrorTitle()}
          </h1>
          <p className="text-lg text-gray-600">
            {getErrorDescription()}
          </p>
        </div>

        {/* Status Information */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
          <h3 className="text-sm font-semibold text-blue-800 mb-2">What's happening?</h3>
          <ul className="text-sm text-blue-700 space-y-1">
            <li>• The worker process has stopped</li>
            <li>• Email verification services are temporarily unavailable</li>
            <li>• Background tasks are not running</li>
            <li>• The system will automatically retry in {countdown} seconds</li>
          </ul>
        </div>

        {/* Possible Causes */}
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
          <h3 className="text-sm font-semibold text-yellow-800 mb-2">Possible Causes:</h3>
          <ul className="text-sm text-yellow-700 space-y-1">
            <li>• Might be Database connection issues</li>
            <li>• System configuration problems</li>
            <li>• Server restart or maintenance</li>
            <li>• Environment mismatch (domain/IP change)</li>
          </ul>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row gap-4 mb-6">
          <button
            onClick={handleRetry}
            disabled={isRetrying}
            className="flex-1 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white font-semibold py-3 px-6 rounded-lg transition-colors duration-200 flex items-center justify-center"
          >
            {isRetrying ? (
              <>
                <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Checking Worker...
              </>
            ) : (
              <>
                <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
                Retry Now
              </>
            )}
          </button>

          <button
            onClick={handleContactSupport}
            className="flex-1 bg-gray-600 hover:bg-gray-700 text-white font-semibold py-3 px-6 rounded-lg transition-colors duration-200 flex items-center justify-center"
          >
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
            </svg>
            Contact Support
          </button>
        </div>

        {/* Footer */}
        <div className="text-center text-sm text-gray-500 border-t pt-6">
          <p className="mb-2">
            <strong>Smart Email Verifier</strong> - Worker Status Check
          </p>
          <p>
            If this issue persists, please contact our support team at support@smartemailverifier.com with your purchase code and system details.
          </p>
        </div>
      </div>
    </div>
  );
};

export default WorkerError; 